import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Steps } from "antd";
import {
  ArrowLeftOutlined,
  FileDoneOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { ApiProblem } from "../../../../api/httpClient";
import { EmptyState } from "../../../../components/EmptyState";
import { ErrorState } from "../../../../components/ErrorState";
import { ForbiddenState } from "../../../../components/ForbiddenState";
import { LoadingSkeleton } from "../../../../components/LoadingSkeleton";
import { StaleState } from "../../../../components/StaleState";
import {
  commitGisImport,
  createGisImport,
  getCemetery,
  getGisImportBatch,
  getGisImportRows,
  type CemeteryData,
  type GisImportBatchData,
  type GisImportRowData,
  type GisImportRowPage,
  type GisImportRowStatus,
  type GisTargetLayer,
} from "../gisApi";
import { GisImportCommitResult } from "./GisImportCommitResult";
import { GisImportReview } from "./GisImportReview";
import { GisImportSource } from "./GisImportSource";
// Dùng CHUNG từ vựng chrome với MÀN A (`.gis-page-header`/`.gis-breadcrumb`/`.gis-header-*`)
// để đi từ màn bản đồ sang đây không bị hẫng — Spec §7.1.
import "../gisAdmin.css";
import "./gisImport.css";

/**
 * MÀN C — Nhập sơ đồ toạ độ GIS từ tệp bản đồ địa lý (SCR-035 / GIS-04 / S12.5.3).
 *
 * Một màn HAI CỘT, không phải ba bước loại trừ nhau: cột trái là vùng tải đứng yên,
 * cột phải là kết quả đối soát, thanh đáy là quyết định nạp. "Đối soát" không phải một
 * bước người dùng làm — nó là kết quả hệ thống trả về ngay khi nhận tệp
 * (`GisImportCommandService.java:114-116`: validate chạy đồng bộ, đi thẳng VALIDATED).
 */

/** Cỡ trang đối soát — khớp mặc định backend (`GisImportController.java:120`). */
const PAGE_SIZE = 20;

function isForbiddenError(error: unknown): boolean {
  if (error instanceof ApiProblem && error.status === 403) return true;
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: unknown }).status === 403
  );
}

function extractRequestId(error: unknown): string | undefined {
  if (error instanceof ApiProblem) return error.problem.requestId;
  if (typeof error === "object" && error !== null && "problem" in error) {
    return (error as { problem?: { requestId?: string } }).problem?.requestId;
  }
  return undefined;
}

/** 409 — batch đã bị người khác nạp; màn giữ nguyên dữ liệu, chỉ thêm băng báo cũ. */
function isConflictError(error: unknown): boolean {
  if (error instanceof ApiProblem && error.status === 409) return true;
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: unknown }).status === 409
  );
}

export function GisImportPage(): React.JSX.Element {
  const { cemeteryId = "" } = useParams<{ cemeteryId: string }>();
  const navigate = useNavigate();

  const [cemetery, setCemetery] = useState<CemeteryData | null>(null);
  const [loadingCemetery, setLoadingCemetery] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [file, setFile] = useState<File | null>(null);
  const [targetLayer, setTargetLayer] = useState<GisTargetLayer>("GRAVE_POSITION");
  const [sourceSrid, setSourceSrid] = useState<number>(4326);

  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState<GisImportBatchData | null>(null);
  const [rows, setRows] = useState<GisImportRowData[]>([]);
  const [meta, setMeta] = useState<GisImportRowPage["meta"] | null>(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<GisImportRowStatus | "ALL">("ALL");
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<GisImportBatchData | null>(null);
  const [stale, setStale] = useState(false);

  const loadCemetery = useCallback(async () => {
    if (!cemeteryId) return;
    setLoadingCemetery(true);
    setForbidden(false);
    setError(null);
    try {
      setCemetery(await getCemetery(cemeteryId));
    } catch (caught: unknown) {
      if (isForbiddenError(caught)) setForbidden(true);
      else setError(caught);
    } finally {
      setLoadingCemetery(false);
    }
  }, [cemeteryId]);

  useEffect(() => {
    void loadCemetery();
  }, [loadCemetery]);

  /**
   * Phân trang chạy ở SERVER: `page` gửi lên là 0-based (`GisImportController.java:119`).
   * Không phân trang client — batch 433 dòng mà chỉ nạp một trang là cắt mất dữ liệu.
   */
  const loadRows = useCallback(
    async (batchId: string, page0: number, filter: GisImportRowStatus | "ALL") => {
      setRowsLoading(true);
      try {
        const page = await getGisImportRows(cemeteryId, batchId, {
          page: page0,
          size: PAGE_SIZE,
          ...(filter === "ALL" ? {} : { status: filter }),
        });
        setRows(page.rows);
        setMeta(page.meta);
      } catch (caught: unknown) {
        setError(caught);
      } finally {
        setRowsLoading(false);
      }
    },
    [cemeteryId],
  );

  /**
   * Chọn/kéo tệp CHÍNH LÀ hành động tải lên — đối soát trả về ngay trong cùng lượt gọi,
   * nên không có nút "tải lên" riêng và không có bước bấm thứ hai.
   */
  const handleFile = useCallback(
    async (chosen: File | null) => {
      setFile(chosen);
      if (!chosen) return;
      setError(null);
      setBatch(null);
      setRows([]);
      setMeta(null);
      setStatusFilter("ALL");
      setCommitResult(null);
      setStale(false);
      setUploading(true);
      try {
        const created = await createGisImport(cemeteryId, chosen, {
          targetLayer,
          sourceSrid,
        });
        setBatch(created);
        await loadRows(created.id, 0, "ALL");
      } catch (caught: unknown) {
        setError(caught);
      } finally {
        setUploading(false);
      }
    },
    [cemeteryId, targetLayer, sourceSrid, loadRows],
  );

  /**
   * Nạp là một lần và không hoàn tác được — không có endpoint rollback trong hợp đồng.
   * Lượt gọi mang khoá idempotency ổn định theo batch (dựng trong `gisApi.commitGisImport`),
   * nên bấm lại sau `PARTIAL_COMMIT` là CÙNG một thao tác logic, backend chỉ xử lý dòng còn thiếu.
   */
  const handleCommit = useCallback(async () => {
    if (!batch) return;
    setCommitting(true);
    setError(null);
    setStale(false);
    try {
      setCommitResult(await commitGisImport(cemeteryId, batch.id));
    } catch (caught: unknown) {
      if (isConflictError(caught)) setStale(true);
      else setError(caught);
    } finally {
      setCommitting(false);
    }
  }, [batch, cemeteryId]);

  /** Lượt gọi thứ tư trong hợp đồng — chỉ dùng khi bấm "Làm mới" ở trạng thái cũ. */
  const refreshBatch = useCallback(async () => {
    if (!batch) return;
    setStale(false);
    try {
      const fresh = await getGisImportBatch(cemeteryId, batch.id);
      setBatch(fresh);
      await loadRows(fresh.id, meta?.page ?? 0, statusFilter);
    } catch (caught: unknown) {
      setError(caught);
    }
  }, [batch, cemeteryId, loadRows, meta, statusFilter]);

  const resetForAnother = useCallback(() => {
    setFile(null);
    setBatch(null);
    setRows([]);
    setMeta(null);
    setStatusFilter("ALL");
    setCommitResult(null);
    setError(null);
    setStale(false);
  }, []);

  if (forbidden) {
    return (
      <div className="gis-import-root">
        <div className="gis-import-single">
          <ForbiddenState
            description="Bạn không có quyền nhập sơ đồ cho nghĩa trang này hoặc nghĩa trang nằm ngoài phạm vi được phân quyền."
            onBack={() => {
              void navigate("/cemeteries");
            }}
          />
        </div>
      </div>
    );
  }

  if (loadingCemetery) {
    return (
      <div className="gis-import-root">
        <div className="gis-import-single">
          <LoadingSkeleton rows={5} />
        </div>
      </div>
    );
  }

  if (error && !cemetery) {
    // Hoist ra const: gọi `extractRequestId` hai lần trong một ternary thì nhánh true không
    // được narrow, và `exactOptionalPropertyTypes` chặn `requestId: string | undefined`.
    const loadRequestId = extractRequestId(error);
    return (
      <div className="gis-import-root">
        <div className="gis-import-single">
          <ErrorState
            title="Không thể mở màn nhập sơ đồ"
            description="Đã xảy ra lỗi khi tải thông tin nghĩa trang."
            onRetry={() => {
              void loadCemetery();
            }}
            {...(loadRequestId ? { requestId: loadRequestId } : {})}
          />
        </div>
      </div>
    );
  }

  const requestId = extractRequestId(error);
  const currentStep = commitResult ? 2 : batch ? 1 : 0;
  const canCommit = batch !== null && batch.status === "VALIDATED";

  return (
    <div className="gis-import-root">
      <div className="gis-page-header">
        <nav className="gis-breadcrumb" aria-label="Đường dẫn">
          <Link to="/cemeteries">Hồ sơ nghĩa trang</Link>
          <span aria-hidden="true">›</span>
          <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}/gis`}>
            {cemetery ? `${cemetery.cemeteryCode} — ${cemetery.name}` : "Sơ đồ mộ & GIS"}
          </Link>
          <span aria-hidden="true">›</span>
          <span>Nhập sơ đồ toạ độ</span>
        </nav>
        <div className="gis-header-row">
          <h1 className="gis-header-title">
            Nhập sơ đồ toạ độ GIS từ tệp bản đồ địa lý
          </h1>
          <div className="gis-header-actions">
            <Link to={`/cemeteries/${encodeURIComponent(cemeteryId)}/gis`}>
              <Button icon={<ArrowLeftOutlined />}>Quay lại sơ đồ</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="gis-import-steps">
        <Steps
          current={currentStep}
          size="small"
          items={[
            { title: "Tải tệp", icon: <UploadOutlined /> },
            { title: "Đối soát", icon: <SyncOutlined /> },
            { title: "Nạp", icon: <FileDoneOutlined /> },
          ]}
        />
      </div>

      <div className="gis-import-body">
        <GisImportSource
          file={file}
          disabled={uploading}
          targetLayer={targetLayer}
          sourceSrid={sourceSrid}
          onFileChange={(chosen) => {
            void handleFile(chosen);
          }}
          onTargetLayerChange={setTargetLayer}
          onSourceSridChange={setSourceSrid}
        />

        <div className="gis-import-result">
          {error ? (
            <ErrorState
              title="Không thể đối soát tệp"
              description="Tệp chưa được ghi vào hệ thống. Sửa tệp nguồn rồi thử lại."
              onRetry={() => {
                void handleFile(file);
              }}
              {...(requestId ? { requestId } : {})}
            />
          ) : (
            <>
              {/* Băng "dữ liệu cũ" đứng TRÊN bảng, không thay thế bảng —
                  dữ liệu đang xem phải giữ nguyên (Spec §8, trạng thái STALE). */}
              {stale ? (
                <StaleState
                  {...(batch?.createdAt ? { lastSyncedAt: batch.createdAt } : {})}
                  onRefresh={() => {
                    void refreshBatch();
                  }}
                />
              ) : null}
              {uploading ? (
                <LoadingSkeleton rows={6} />
              ) : commitResult ? (
                <GisImportCommitResult batch={commitResult} />
              ) : batch ? (
                <GisImportReview
                  batch={batch}
                  rows={rows}
                  meta={meta}
                  loading={rowsLoading}
                  statusFilter={statusFilter}
                  onStatusFilterChange={(value) => {
                    setStatusFilter(value);
                    void loadRows(batch.id, 0, value);
                  }}
                  onPageChange={(page0) => {
                    void loadRows(batch.id, page0, statusFilter);
                  }}
                />
              ) : (
                <EmptyState
                  title="Chưa có tệp nào được đối soát"
                  description="Chọn hoặc kéo tệp GeoJSON vào cột bên trái để bắt đầu."
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="gis-import-footer">
        {commitResult ? (
          <>
            <Button onClick={resetForAnother}>Nhập tệp khác</Button>
            {commitResult.status === "PARTIAL_COMMIT" ? (
              <Button
                type="primary"
                loading={committing}
                onClick={() => {
                  void handleCommit();
                }}
              >
                Nạp lại phần còn thiếu
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  void navigate(`/cemeteries/${encodeURIComponent(cemeteryId)}/gis`);
                }}
              >
                Về sơ đồ
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                void navigate(`/cemeteries/${encodeURIComponent(cemeteryId)}/gis`);
              }}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              disabled={!canCommit}
              loading={committing}
              onClick={() => {
                void handleCommit();
              }}
            >
              {batch ? `Nạp ${batch.validRows ?? 0} dòng hợp lệ` : "Nạp dữ liệu"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
